import { c as _c } from "react/compiler-runtime";
import { IcoChevronLeftBig16, IcoChevronRightBig16, IcoMaximize16 } from '@onefootprint/icons';
import type { EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { IconButton, LoadingSpinner, Tooltip } from '@onefootprint/ui';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEntityId from '@/entity/hooks/use-entity-id';
import useEntityKind from '@/entity/hooks/use-entity-kind';
import { useOpenPage } from '@/hooks/use-open-page';
import { CURRENT_ONBOARDING_ID, CURRENT_TAB_ID } from '../../constants';
import OnboardingTab from './onboarding-tab';
import OnboardingTabLabel from './onboarding-tab-label';
type OnboardingTabsProps = {
  onboardings: EntityOnboarding[];
  selectedId: string;
  onSelect: (id: string) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
};
const LOAD_MORE_THRESHOLD = 0.9;
const OnboardingTabs = t0 => {
  const $ = _c(80);
  const {
    onboardings,
    selectedId,
    onSelect,
    onLoadMore,
    hasMore,
    isLoadingMore
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const entityId = useEntityId();
  const entityKind = useEntityKind();
  const handleOpenPage = useOpenPage();
  const listRef = useRef(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const tabRefs = useRef(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      start: false,
      end: false
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [overflow, setOverflow] = useState(t3);
  let t4;
  if ($[3] !== hasMore || $[4] !== isLoadingMore || $[5] !== onLoadMore) {
    t4 = () => {
      const list = listRef.current;
      if (!list) {
        return;
      }
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = list;
      const start = scrollLeft > 0;
      const end = scrollLeft + clientWidth < scrollWidth - 1;
      setOverflow(prev => prev.start === start && prev.end === end ? prev : {
        start,
        end
      });
      if (hasMore && !isLoadingMore && (scrollLeft + clientWidth) / scrollWidth >= LOAD_MORE_THRESHOLD) {
        onLoadMore();
      }
    };
    $[3] = hasMore;
    $[4] = isLoadingMore;
    $[5] = onLoadMore;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const checkScroll = t4;
  let t5;
  if ($[7] !== hasMore || $[8] !== isLoadingMore || $[9] !== onboardings.length) {
    t5 = [onboardings.length, hasMore, isLoadingMore];
    $[7] = hasMore;
    $[8] = isLoadingMore;
    $[9] = onboardings.length;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  useEffect(checkScroll, t5);
  let t6;
  if ($[11] !== checkScroll) {
    t6 = () => {
      const list_0 = listRef.current;
      if (!list_0) {
        return;
      }
      const observer = new ResizeObserver(checkScroll);
      observer.observe(list_0);
      return () => observer.disconnect();
    };
    $[11] = checkScroll;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== hasMore || $[14] !== isLoadingMore) {
    t7 = [hasMore, isLoadingMore];
    $[13] = hasMore;
    $[14] = isLoadingMore;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[16] !== selectedId) {
    t8 = () => {
      const list_1 = listRef.current;
      const tab = tabRefs.current.get(selectedId);
      if (!list_1 || !tab) {
        return;
      }
      const isVisible = tab.offsetLeft >= list_1.scrollLeft && tab.offsetLeft + tab.offsetWidth <= list_1.scrollLeft + list_1.clientWidth;
      if (!isVisible) {
        list_1.scrollTo({
          left: tab.offsetLeft - (list_1.clientWidth - tab.offsetWidth) / 2,
          behavior: "smooth"
        });
      }
    };
    t9 = [selectedId];
    $[16] = selectedId;
    $[17] = t8;
    $[18] = t9;
  } else {
    t8 = $[17];
    t9 = $[18];
  }
  useEffect(t8, t9);
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = direction => {
      const list_2 = listRef.current;
      list_2?.scrollBy({
        left: direction * list_2.clientWidth * 0.8,
        behavior: "smooth"
      });
    };
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const scrollByPage = t10;
  let t11;
  if ($[20] !== t) {
    t11 = t(_temp);
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = <p className="text-snippet-3 text-tertiary">{t11}</p>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t) {
    t13 = t(_temp2);
    $[24] = t;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13) {
    t14 = <div className="text-label-2 text-primary">{t13}</div>;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t) {
    t15 = t(_temp3);
    $[28] = t;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t15) {
    t16 = <div className="text-label-2 text-tertiary">{t15}</div>;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t12 || $[33] !== t14 || $[34] !== t16) {
    t17 = <div className="flex flex-col gap-1 text-left">{t12}{t14}{t16}</div>;
    $[32] = t12;
    $[33] = t14;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  if ($[36] !== checkScroll || $[37] !== entityId || $[38] !== entityKind?.urlPath || $[39] !== handleOpenPage || $[40] !== isLoadingMore || $[41] !== onSelect || $[42] !== onboardings || $[43] !== selectedId || $[44] !== t || $[45] !== t17) {
    const tabs = [{
      id: CURRENT_ONBOARDING_ID,
      domId: CURRENT_TAB_ID,
      label: t17,
      hasTrace: false
    }, ...onboardings.map(_temp4)];
    const handleKeyDown = (event, index) => {
      const targetIndex = {
        ArrowRight: Math.min(index + 1, tabs.length - 1),
        ArrowLeft: Math.max(index - 1, 0),
        Home: 0,
        End: tabs.length - 1
      }[event.key];
      if (targetIndex === undefined) {
        return;
      }
      event.preventDefault();
      tabRefs.current.get(tabs[targetIndex].id)?.focus();
    };
    t26 = "relative";
    t18 = listRef;
    t19 = "tablist";
    if ($[55] !== t) {
      t20 = t(_temp5);
      $[55] = t;
      $[56] = t20;
    } else {
      t20 = $[56];
    }
    t21 = "horizontal";
    t22 = isLoadingMore;
    t23 = "flex gap-2 overflow-x-auto p-0.5 scroll-px-16 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";
    t24 = checkScroll;
    t25 = tabs.map((t27, index_0) => {
      const {
        id,
        domId,
        label,
        hasTrace
      } = t27;
      const isSelected = id === selectedId;
      return <div key={id} className="grid shrink-0 [&>*]:[grid-area:1/1]"><OnboardingTab id={domId} ref={node => {
          if (node) {
            tabRefs.current.set(id, node);
          } else {
            tabRefs.current.delete(id);
          }
        }} isSelected={isSelected} onClick={() => onSelect(id)} onKeyDown={event_0 => handleKeyDown(event_0, index_0)}>{label}</OnboardingTab>{isSelected && hasTrace && <div aria-hidden={true} className="self-end justify-self-end m-2"><Tooltip content={t(_temp6)}><IconButton aria-label={t(_temp7)} icon={IcoMaximize16} variant="outline" size="tiny" tabIndex={-1} onClick={event_1 => handleOpenPage(event_1, `/${entityKind?.urlPath}/${entityId}/onboardings/${id}/trace`)} /></Tooltip></div>}</div>;
    });
    $[36] = checkScroll;
    $[37] = entityId;
    $[38] = entityKind?.urlPath;
    $[39] = handleOpenPage;
    $[40] = isLoadingMore;
    $[41] = onSelect;
    $[42] = onboardings;
    $[43] = selectedId;
    $[44] = t;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
    $[53] = t25;
    $[54] = t26;
  } else {
    t18 = $[46];
    t19 = $[47];
    t20 = $[48];
    t21 = $[49];
    t22 = $[50];
    t23 = $[51];
    t24 = $[52];
    t25 = $[53];
    t26 = $[54];
  }
  let t27;
  if ($[57] !== isLoadingMore) {
    t27 = isLoadingMore && <div aria-hidden={true} className="flex items-center px-4"><LoadingSpinner size="compact" /></div>;
    $[57] = isLoadingMore;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t18 || $[60] !== t19 || $[61] !== t20 || $[62] !== t21 || $[63] !== t22 || $[64] !== t23 || $[65] !== t24 || $[66] !== t25 || $[67] !== t27) {
    t28 = <div ref={t18} role={t19} aria-label={t20} aria-orientation={t21} aria-busy={t22} className={t23} onScroll={t24}>{t25}{t27}</div>;
    $[59] = t18;
    $[60] = t19;
    $[61] = t20;
    $[62] = t21;
    $[63] = t22;
    $[64] = t23;
    $[65] = t24;
    $[66] = t25;
    $[67] = t27;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== overflow.start || $[70] !== t) {
    t29 = overflow.start && <div className="absolute inset-y-0 left-0 flex items-center w-16 pointer-events-none bg-gradient-to-r from-primary to-transparent"><IconButton aria-label={t(_temp8)} icon={IcoChevronLeftBig16} variant="outline" size="compact" rounded={true} className="pointer-events-auto" tabIndex={-1} onClick={() => scrollByPage(-1)} /></div>;
    $[69] = overflow.start;
    $[70] = t;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== overflow.end || $[73] !== t) {
    t30 = overflow.end && <div className="absolute inset-y-0 right-0 flex items-center justify-end w-16 pointer-events-none bg-gradient-to-l from-primary to-transparent"><IconButton aria-label={t(_temp9)} icon={IcoChevronRightBig16} variant="outline" size="compact" rounded={true} className="pointer-events-auto" tabIndex={-1} onClick={() => scrollByPage(1)} /></div>;
    $[72] = overflow.end;
    $[73] = t;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== t26 || $[76] !== t28 || $[77] !== t29 || $[78] !== t30) {
    t31 = <div className={t26}>{t28}{t29}{t30}</div>;
    $[75] = t26;
    $[76] = t28;
    $[77] = t29;
    $[78] = t30;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  return t31;
};
export default OnboardingTabs;
function _temp($) {
  return $.currentTab.eyebrow;
}
function _temp2($_0) {
  return $_0.current;
}
function _temp3($_1) {
  return $_1.currentTab.description;
}
function _temp4(onboarding) {
  return {
    id: onboarding.id,
    domId: onboarding.id,
    label: <OnboardingTabLabel onboarding={onboarding} />,
    hasTrace: onboarding.hasGraphTrace
  };
}
function _temp5($_2) {
  return $_2.title;
}
function _temp6($_3) {
  return $_3.onboardingTrace.showExpandedView;
}
function _temp7($_4) {
  return $_4.onboardingTrace.showExpandedView;
}
function _temp8($_5) {
  return $_5.newerOnboardings;
}
function _temp9($_6) {
  return $_6.olderOnboardings;
}